import React from 'react';
import {SKILLS_BY_ID, localized} from '../../data/traits';
import {DISCIPLINES_BY_ID} from '../../data/disciplines';
import {vtmT} from '../../i18n';

const range = (count) => Array.from({length: count}, (_, index) => index);

/**
 * Sélecteur de points : cliquer le point n fixe la valeur à n, recliquer le dernier point plein retire un point.
 * @param {Number} [min] valeur plancher (1 pour les attributs)
 */
export const DotPicker = ({value, onChange, max = 5, min = 0, label}) => (
  <div className="vtmDotPicker" role="group" aria-label={`${label} : ${value}`}>
    {range(max).map(index => {
      const target = index + 1 === value ? index : index + 1;
      return (
        <button
          key={index}
          type="button"
          className={`vtmDotButton${index < value ? ' on' : ''}`}
          onClick={() => onChange(Math.max(min, target))}
          aria-label={`${label} ${index + 1}`}
          aria-pressed={index < value}
        >
          <span aria-hidden="true">●</span>
        </button>
      );
    })}
  </div>
);

/**
 * Compteur de répartition : pour chaque valeur, combien de traits l'ont / combien sont attendus.
 * @param {Object<Number, Number>} distribution attendu, ex : {4: 1, 3: 3, 2: 4, 1: 1}
 * @param {Number[]} values valeurs actuelles des traits
 */
export const DistributionCounter = ({distribution, values}) => (
  <div className="vtmDistribution" aria-label={vtmT('distribution')}>
    {Object.keys(distribution).map(Number).sort((a, b) => b - a).map(dots => {
      const have = values.filter(value => value === dots).length;
      const want = distribution[dots];
      const state = have === want ? 'ok' : (have > want ? 'over' : 'under');
      return (
        <span key={dots} className={`vtmDistributionChip ${state}`}>
          <span className="vtmDistributionDots" aria-hidden="true">{'●'.repeat(dots)}</span>
          <span>{have} / {want}</span>
        </span>
      );
    })}
  </div>
);

const errorParams = (error, lng) => ({
  ...error,
  skill: error.skill ? localized(SKILLS_BY_ID[error.skill]?.name, lng) : '',
  discipline: error.discipline ? localized(DISCIPLINES_BY_ID[error.discipline]?.name, lng) : '',
});

/** Message lisible pour une erreur de validation. */
export const errorMessage = (stepId, error, lng) => {
  if (!error.reason) return vtmT('err_distribution', errorParams(error, lng));
  if (stepId === 'skills' && error.reason === 'missingPredator') return vtmT('err_missingPredator_skills');
  return vtmT(`err_${error.reason}`, errorParams(error, lng));
};

export const StepErrors = ({stepId, status, lng}) => {
  if (!status || status.valid) return null;
  const messages = [...new Set(status.errors.map(error => errorMessage(stepId, error, lng)))];
  return (
    <ul className="vtmStepErrors" aria-live="polite">
      {messages.map(message => <li key={message}>{message}</li>)}
    </ul>
  );
};

export const TextField = ({id, label, value, onChange, placeholder, multiline = false}) => (
  <div className="vtmField">
    <label className="vtmFieldLabel" htmlFor={id}>{label}</label>
    {multiline ? (
      <textarea id={id} value={value || ''} placeholder={placeholder} rows={2} onChange={event => onChange(event.target.value)} />
    ) : (
      <input id={id} type="text" value={value || ''} placeholder={placeholder} onChange={event => onChange(event.target.value)} />
    )}
  </div>
);
